import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import apiClient, { setAdminAccessToken, clearAdminTokens } from '@/api/client'
import type { AdminUser, AdminAuthResponse, AdminMeResponse, LicenseStatus } from '@/types/admin'

export type { AdminUser, LicenseStatus }

export const useAdminStore = defineStore('admin', () => {
  const admin = ref<AdminUser | null>(null)
  const licenseStatus = ref<LicenseStatus | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  let initPromise: Promise<void> | null = null

  // Getters
  const isAuthenticated = computed(() => admin.value !== null)
  const role = computed(() => admin.value?.role ?? '')
  const permissions = computed(() => admin.value?.permissions ?? [])
  const name = computed(() => admin.value?.name ?? '')
  const initials = computed(() => {
    if (!admin.value?.name) return ''
    return admin.value.name
      .split(' ')
      .map((w) => w[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  })

  // Backward-compatible: expose admin as `user` for any consumers expecting it
  const user = computed(() => {
    if (!admin.value) return null
    return {
      ...admin.value,
      isActive: admin.value.is_active,
    }
  })

  function hasRole(requiredRoles: string[]): boolean {
    if (!admin.value?.role) return false
    return requiredRoles.includes(admin.value.role)
  }

  function hasPermission(requiredPermissions: string[]): boolean {
    if (!admin.value) return false
    return requiredPermissions.some((p) => admin.value!.permissions.includes(p))
  }

  function setAuthData(data: AdminAuthResponse) {
    admin.value = data.admin
    setAdminAccessToken(data.access_token)
  }

  async function fetchLicenseStatus(): Promise<void> {
    try {
      const response = await apiClient.get<LicenseStatus>('/admin/api/license/status')
      licenseStatus.value = response.data
    } catch {
      // Non-blocking — license status may not be available yet (fresh install)
      licenseStatus.value = null
    }
  }

  async function login(
    email: string,
    password: string,
    twoFactorCode?: string,
  ): Promise<{ requires_2fa?: boolean }> {
    isLoading.value = true
    error.value = null

    try {
      const response = await apiClient.post('/admin/api/auth/login', {
        email,
        password,
        two_factor_code: twoFactorCode,
      })

      if (response.data.requires_2fa) {
        return { requires_2fa: true }
      }

      setAuthData(response.data as AdminAuthResponse)
      void fetchLicenseStatus()
      return {}
    } catch (e: unknown) {
      const axiosError = e as { response?: { data?: { message?: string }; status?: number } }
      error.value = axiosError.response?.data?.message ?? 'Login failed'
      throw e
    } finally {
      isLoading.value = false
    }
  }

  async function logout(): Promise<void> {
    try {
      await apiClient.post('/admin/api/auth/logout')
    } catch {
      // Ignore logout errors — clear state regardless
    } finally {
      admin.value = null
      licenseStatus.value = null
      error.value = null
      clearAdminTokens()
    }
  }

  async function fetchProfile(): Promise<void> {
    try {
      const response = await apiClient.get<AdminMeResponse>('/admin/api/auth/me')
      admin.value = response.data.admin
    } catch (e: unknown) {
      const axiosError = e as { response?: { status?: number } }
      if (axiosError.response?.status === 401) {
        admin.value = null
        clearAdminTokens()
      }
    }
  }

  async function initialize(): Promise<void> {
    // No stored token — nothing to restore
    if (!localStorage.getItem('admin_access_token')) {
      return
    }

    isLoading.value = true
    try {
      // Use stored token to fetch profile; the response interceptor
      // handles transparent refresh if the token has expired.
      await fetchProfile()
      if (admin.value) {
        void fetchLicenseStatus()
      }
    } catch {
      // Token invalid or expired beyond refresh — clear session
      admin.value = null
      clearAdminTokens()
    } finally {
      isLoading.value = false
    }
  }

  /** Wait for initialization to complete (used by router guards). */
  function waitForInit(): Promise<void> {
    if (!initPromise) {
      initPromise = initialize()
    }
    return initPromise
  }

  return {
    // State
    admin,
    licenseStatus,
    user,
    isLoading,
    error,
    // Getters
    isAuthenticated,
    role,
    permissions,
    name,
    initials,
    // Actions
    login,
    logout,
    fetchProfile,
    fetchLicenseStatus,
    initialize,
    waitForInit,
    hasRole,
    hasPermission,
  }
})
